import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import {
	Button,
	FabList,
	HookFormInputGroup,
	HookFormPhoneInput,
	MerchantsPreAppUrl,
} from "components";
import useDirtyWatcher from "components/hookForms/useDirtyWatcher";
import { LineDivider } from "components/layout";
import { ActiveUserStatus, UserProfileFields } from "globalConstants";
import { isFeatureEnabled } from "helpers";
import {
	alphaNumericNormalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import {
	matchesNameRegex,
	matchesAddressRegex,
	matchesCityRegex,
	zipField,
} from "validators/userValidators";
import {
	dateOfBirth1900,
	dateOfBirthMoreThan18,
	dateValidation,
	maxLength2,
	maxLength30,
	maxLength50,
	maxLength9,
	minLength2,
	minLength5,
	onlyDate,
	onlyLetters,
	onlyTwoUpperCaseLetters,
	required,
} from "validators/validation";

import EditableEmailField from "../formSections/editableEmailField";
import EditableMobilePhoneField from "../formSections/editableMobilePhoneField";
import JarisUrl from "../formSections/jarisUrl";

import { ReactComponent as SaveIcon } from "static/images/save.svg";

type UserProfileFormProps = {
	defaultValues: { [key: string]: any } | undefined;
	isLoading: boolean;
	onSubmit: Function;
};

const UserProfileForm = ({
	defaultValues,
	isLoading,
	onSubmit,
}: UserProfileFormProps) => {
	const {
		FirstName,
		LastName,
		DateOfBirth,
		Phone,
		Address,
		City,
		State,
		Zip,
		Country,
		Status,
	} = UserProfileFields;

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		values: defaultValues,
	});
	const {
		control,
		watch,
		handleSubmit,
		register,
		formState: { errors },
	} = formMethods;
	const isDirty = useDirtyWatcher({ control, initialValues: defaultValues });

	const handleSubmitForm = formValues => {
		onSubmit(formValues);
	};

	const [url, setUrl] = useState<string>("");
	const domain = defaultValues?.domain;
	const alias = watch("alias");

	useEffect(() => {
		setUrl(`https://${domain || "[domain]"}/${alias || "[alias]"}`);
	}, [domain, alias]);

	const isActiveUser = defaultValues?.[Status.name] === ActiveUserStatus;

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={handleSubmit(handleSubmitForm)}>
				<EditableEmailField
					email={defaultValues?.email}
					isLoading={isLoading}
					allowedDomains={
						defaultValues?.companyWhitelistedEmailDomains
					}
				/>

				<LineDivider className="w-90" />

				<Row>
					<Col lg={5}>
						<HookFormInputGroup
							name={FirstName.name}
							register={register}
							validator={{
								required,
								minLength2,
								maxLength30,
								matchesNameRegex,
							}}
							colLabel={5}
							colInput={7}
							label={FirstName.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={trimMidSentenceNormalizer}
							blurLoading={isLoading}
							showRequiredMark
						/>
					</Col>
					<Col
						lg={{
							span: 5,
							offset: 2,
						}}
					>
						<HookFormInputGroup
							name={LastName.name}
							register={register}
							validator={{
								required,
								minLength2,
								maxLength30,
								matchesNameRegex,
							}}
							colLabel={5}
							colInput={7}
							label={LastName.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={trimMidSentenceNormalizer}
							blurLoading={isLoading}
							showRequiredMark
						/>
					</Col>
				</Row>
				<Row>
					<Col
						lg={{
							span: 5,
							offset: 0,
						}}
					>
						<HookFormInputGroup
							name={DateOfBirth.name}
							register={register}
							validator={{
								dateValidation,
								dateOfBirth1900,
								dateOfBirthMoreThan18,
							}}
							colLabel={5}
							colInput={7}
							label={DateOfBirth.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={onlyDate}
							blurLoading={isLoading}
							placeholder="MM/DD/YYYY"
						/>
					</Col>
				</Row>
				<Row>
					<Col lg={5}>
						<HookFormPhoneInput
							name={Phone.name}
							label={Phone.displayName}
							isLoading={isLoading}
							colLabel={5}
							colInput={7}
						/>
					</Col>
					<Col
						lg={{
							span: 5,
							offset: 2,
						}}
					>
						<EditableMobilePhoneField
							isLoading={isLoading}
							mobilePhoneDefault={defaultValues?.mobilePhone}
						/>
					</Col>
				</Row>

				<Row>
					<Col lg={5}>
						<HookFormInputGroup
							name={Address.name}
							register={register}
							validator={{
								minLength2,
								maxLength50,
								matchesAddressRegex,
							}}
							colLabel={5}
							colInput={7}
							label={Address.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={trimMidSentenceNormalizer}
							blurLoading={isLoading}
						/>
					</Col>
					<Col
						lg={{
							span: 5,
							offset: 2,
						}}
					>
						<HookFormInputGroup
							name={City.name}
							register={register}
							validator={{
								minLength2,
								maxLength30,
								matchesCityRegex,
							}}
							colLabel={5}
							colInput={7}
							label={City.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={trimMidSentenceNormalizer}
							blurLoading={isLoading}
						/>
					</Col>
				</Row>
				<Row>
					<Col lg={5}>
						<HookFormInputGroup
							name={State.name}
							register={register}
							validator={{
								minLength2,
								maxLength2,
							}}
							colLabel={5}
							colInput={7}
							label={State.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={onlyTwoUpperCaseLetters}
							blurLoading={isLoading}
						/>
					</Col>
					<Col
						lg={{
							span: 5,
							offset: 2,
						}}
					>
						<HookFormInputGroup
							name={Zip.name}
							register={register}
							validator={{
								minLength5,
								maxLength9,
								zipField,
							}}
							colLabel={5}
							colInput={7}
							label={Zip.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={alphaNumericNormalizer}
							blurLoading={isLoading}
						/>
					</Col>
				</Row>
				<Row>
					<Col lg={5}>
						<HookFormInputGroup
							name={Country.name}
							register={register}
							validator={{ maxLength30 }}
							colLabel={5}
							colInput={7}
							label={Country.displayName}
							errors={errors}
							normalizeOnBlur={trimNormalizer}
							normalize={onlyLetters}
							blurLoading={isLoading}
						/>
					</Col>
				</Row>

				<MerchantsPreAppUrl
					loading={isLoading}
					defaultValues={defaultValues}
					url={url}
					alias={alias}
					isActiveUser={isActiveUser}
				/>

				{isFeatureEnabled("MMS-196-Jaris-POC") && (
					<JarisUrl
						isLoading={isLoading}
						defaultValues={defaultValues}
					/>
				)}

				<FabList>
					<Button
						value="Save"
						size="lg"
						shape="circle"
						icon={<SaveIcon />}
						type="submit"
						isLoading={isLoading}
						disabled={!isDirty}
					/>
				</FabList>
			</form>
		</FormProvider>
	);
};

export default UserProfileForm;
